import { useEffect, useState } from 'react';
import { CheckIcon, LockIcon } from 'lucide-react';
import type { ChoiceGroup, FoodOption, NutritionInfo } from '@/data/meal-plan';
import type { ChoiceGroupSelections, Selection } from '@/types/meal-plan-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOptionalWeekPlan } from '@/context/week-plan-context';

const CATEGORY_STYLES: Record<ChoiceGroup['category'], string> = {
  carb: 'bg-amber-100 text-amber-800',
  protein: 'bg-red-100 text-red-800',
  fat: 'bg-purple-100 text-purple-800',
  fruit: 'bg-orange-100 text-orange-800',
  vegetable: 'bg-green-100 text-green-800',
};

const CATEGORY_LABELS: Record<ChoiceGroup['category'], string> = {
  carb: 'Carbohidrato',
  protein: 'Proteína',
  fat: 'Grasa',
  fruit: 'Fruta',
  vegetable: 'Vegetal',
};

interface FoodOptionPickerProps {
  group: ChoiceGroup;
  selections: ChoiceGroupSelections;
  onChange: (selections: ChoiceGroupSelections) => void;
  /**
   * A food a menu includes as is (single-option group): shown as an included
   * item, with no "Elige N" hint and no way to change or clear it.
   */
  fixed?: boolean;
}

/** Nutrition pills for selected ingredient display */
function NutritionPills({ n }: { n: NutritionInfo }) {
  const p = n.perPortion;
  return (
    <div className="flex items-center gap-1.5 text-[10px] tabular-nums">
      <span className="font-medium text-foreground/70">
        {p.calories}
        <span className="text-muted-foreground ml-0.5">cal</span>
      </span>
      <span className="text-muted-foreground/40">·</span>
      <span className="text-red-600/70">
        {p.protein}
        <span className="text-muted-foreground ml-0.5">P</span>
      </span>
      <span className="text-muted-foreground/40">·</span>
      <span className="text-amber-600/70">
        {p.carbs}
        <span className="text-muted-foreground ml-0.5">C</span>
      </span>
      <span className="text-muted-foreground/40">·</span>
      <span className="text-purple-600/70">
        {p.fat}
        <span className="text-muted-foreground ml-0.5">G</span>
      </span>
    </div>
  );
}

/** Macro line for dropdown items */
function DropdownMacros({ n }: { n: NutritionInfo }) {
  const p = n.perPortion;
  return (
    <span className="text-[10px] text-muted-foreground/70 tabular-nums block mt-0.5">
      {p.calories} cal · {p.protein}p · {p.carbs}c · {p.fat}g
    </span>
  );
}

/** Full nutrition detail dialog */
function NutritionDialog({
  opt,
  onClose,
}: {
  opt: FoodOption;
  onClose: () => void;
}) {
  const n = opt.nutrition!;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-background rounded-t-2xl sm:rounded-2xl w-full max-w-sm mx-auto p-5 space-y-4 safe-area-bottom"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="space-y-1">
          <h3 className="font-semibold text-base">{opt.name}</h3>
          <p className="text-xs text-muted-foreground">
            {opt.portion}
            {opt.cookedGrams != null ? ` → ${opt.cookedGrams} g cocido` : ''}
          </p>
          <p className="text-[11px] text-muted-foreground/60">{n.sourceName}</p>
        </div>

        {/* Per portion */}
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Por porción ({opt.portion})
          </h4>
          <div className="grid grid-cols-4 gap-2">
            <MacroCard
              label="Calorías"
              value={n.perPortion.calories}
              unit="kcal"
            />
            <MacroCard label="Proteína" value={n.perPortion.protein} unit="g" />
            <MacroCard label="Carbos" value={n.perPortion.carbs} unit="g" />
            <MacroCard label="Grasa" value={n.perPortion.fat} unit="g" />
          </div>
        </div>

        {/* Per 100g */}
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Por 100 g
          </h4>
          <div className="grid grid-cols-4 gap-2">
            <MacroCard
              label="Calorías"
              value={n.per100g.calories}
              unit="kcal"
            />
            <MacroCard label="Proteína" value={n.per100g.protein} unit="g" />
            <MacroCard label="Carbos" value={n.per100g.carbs} unit="g" />
            <MacroCard label="Grasa" value={n.per100g.fat} unit="g" />
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full text-center text-sm font-medium text-muted-foreground py-2 hover:text-foreground transition-colors"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}

function MacroCard({
  label,
  value,
  unit,
}: {
  label: string;
  value: number;
  unit: string;
}) {
  return (
    <div className="rounded-lg bg-muted/50 p-2 text-center">
      <div className="text-sm font-semibold tabular-nums">
        {value}
        <span className="text-[10px] text-muted-foreground ml-0.5">{unit}</span>
      </div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

export function FoodOptionPicker({
  group,
  selections,
  onChange,
  fixed = false,
}: FoodOptionPickerProps) {
  const { pick, canRepeat, optional, options, note, category } = group;
  const [dialogOpt, setDialogOpt] = useState<FoodOption | null>(null);
  // "Comido" is the owner's log: in friend mode the planner sees it, read-only.
  const weekPlan = useOptionalWeekPlan();
  const canMarkEaten = weekPlan?.canMarkEaten ?? true;
  const lockedHint = `Solo ${weekPlan?.owner.username ?? ''} marca lo que comió.`;
  // Read-only "comido": tapping the lock says why, for a few seconds.
  const [hintSlot, setHintSlot] = useState<number | null>(null);
  useEffect(() => {
    if (hintSlot === null) return;
    const timer = setTimeout(() => setHintSlot(null), 4000);
    return () => clearTimeout(timer);
  }, [hintSlot]);

  // Expand selections into `pick` slots for rendering (using optionId)
  const slots: (string | null)[] = [];
  if (!selections.skipped) {
    for (const sel of selections.selections) {
      for (let i = 0; i < sel.count; i++) {
        slots.push(sel.optionId);
      }
    }
  }
  while (slots.length < pick) slots.push(null);

  // Expand eaten state to match slots
  const eatenSlots = selections.eaten ?? [];

  function handleSlotChange(slotIndex: number, value: string) {
    const optionId = value === '__clear__' ? null : value;
    const newSlots = [...slots];
    newSlots[slotIndex] = optionId;

    // Collapse slots back into Selection[]
    const counts = new Map<string, number>();
    for (const id of newSlots) {
      if (id !== null) {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
    }
    const newSelections: Selection[] = [];
    for (const [id, count] of counts) {
      newSelections.push({ optionId: id, count });
    }
    // Preserve eaten state, clear for cleared slots
    const newEaten = [...eatenSlots];
    if (optionId === null) newEaten[slotIndex] = false;
    onChange({ selections: newSelections, skipped: false, eaten: newEaten });
  }

  function handleEatenToggle(slotIndex: number) {
    const newEaten = [...eatenSlots];
    newEaten[slotIndex] = !newEaten[slotIndex];
    onChange({ ...selections, eaten: newEaten });
  }

  function handleSkipToggle() {
    onChange({
      selections: selections.skipped ? [] : selections.selections,
      skipped: !selections.skipped,
      eaten: selections.skipped ? [] : selections.eaten,
    });
  }

  // Which IDs are already taken (for canRepeat: false filtering)
  const takenIds = new Set(
    !canRepeat ? slots.filter((s): s is string => s !== null) : []
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${CATEGORY_STYLES[category]}`}
        >
          {CATEGORY_LABELS[category]}
        </span>
        {!fixed && (
          <span className="text-xs text-muted-foreground">
            Elige {pick}
            {canRepeat ? ' (puedes repetir)' : ''}
          </span>
        )}
        {optional && (
          <button
            type="button"
            onClick={handleSkipToggle}
            className={`ml-auto text-xs px-2 py-0.5 rounded border transition-colors ${
              selections.skipped
                ? 'bg-muted text-muted-foreground border-border'
                : 'bg-background text-foreground border-border hover:bg-muted'
            }`}
          >
            {selections.skipped ? 'Omitido' : 'Omitir'}
          </button>
        )}
      </div>

      {!selections.skipped && (
        <div className="grid gap-2">
          {slots.map((currentValue, slotIndex) => {
            const availableOptions = options.filter(
              (opt) =>
                canRepeat || !takenIds.has(opt.id) || opt.id === currentValue,
            );
            const isEaten = !!eatenSlots[slotIndex];
            const selectedOpt =
              currentValue !== null
                ? options.find((o) => o.id === currentValue) ?? null
                : null;

            // Empty slot — just show the select dropdown
            if (!selectedOpt) {
              return (
                <Select
                  key={slotIndex}
                  value={undefined}
                  onValueChange={(v) => handleSlotChange(slotIndex, v)}
                >
                  <SelectTrigger size="sm" className="w-full">
                    <SelectValue placeholder="Elige…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        <div>
                          {opt.name}{' '}
                          <span className="text-muted-foreground">
                            ({opt.portion}
                            {opt.cookedGrams != null &&
                              ` → ${opt.cookedGrams} g cocido`}
                            )
                          </span>
                          {opt.nutrition && (
                            <DropdownMacros n={opt.nutrition} />
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }

            // Filled slot — render ingredient card with nutrition outside the select
            return (
              <div
                key={slotIndex}
                className={`rounded-lg border px-3 py-2 transition-opacity`}
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate`}>
                      {selectedOpt.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {selectedOpt.portion}
                      {selectedOpt.cookedGrams != null &&
                        ` → ${selectedOpt.cookedGrams} g cocido`}
                    </div>
                  </div>
                  {!fixed && (
                    <Select
                      value={currentValue}
                      onValueChange={(v) => handleSlotChange(slotIndex, v)}
                    >
                      <SelectTrigger
                        size="sm"
                        className="w-auto gap-1 px-2 shrink-0 border-0 bg-muted/50 h-7 text-xs"
                      >
                        <span className="text-muted-foreground">Cambiar</span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          value="__clear__"
                          className="text-muted-foreground italic"
                        >
                          Limpiar selección
                        </SelectItem>
                        {availableOptions.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            <div>
                              {opt.name}{' '}
                              <span className="text-muted-foreground">
                                ({opt.portion}
                                {opt.cookedGrams != null &&
                                  ` → ${opt.cookedGrams} g cocido`}
                                )
                              </span>
                              {opt.nutrition && (
                                <DropdownMacros n={opt.nutrition} />
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {canMarkEaten ? (
                    <input
                      type="checkbox"
                      checked={isEaten}
                      onChange={() => handleEatenToggle(slotIndex)}
                      className="size-4 shrink-0 rounded border-border accent-green-600"
                      title="Comido"
                      aria-label="Comido"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setHintSlot(hintSlot === slotIndex ? null : slotIndex)}
                      aria-label={`${isEaten ? 'Comido' : 'No marcado como comido'}. ${lockedHint}`}
                      title={lockedHint}
                      className="-my-1.5 -mr-1.5 grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted"
                    >
                      {isEaten ? (
                        <CheckIcon aria-hidden="true" className="size-4 text-green-600" />
                      ) : (
                        <LockIcon aria-hidden="true" className="size-3.5 opacity-60" />
                      )}
                    </button>
                  )}
                </div>
                {hintSlot === slotIndex && (
                  <p role="status" className="mt-1 text-xs text-muted-foreground">
                    {lockedHint}
                  </p>
                )}
                {/* Nutrition info — tappable to open detail dialog */}
                {selectedOpt.nutrition && (
                  <button
                    type="button"
                    onClick={() => setDialogOpt(selectedOpt)}
                    className="mt-1.5 flex items-center gap-3 w-full text-left rounded-md bg-muted/30 px-2 py-1.5 hover:bg-muted/60 transition-colors"
                  >
                    <NutritionPills n={selectedOpt.nutrition} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {note && <p className="text-xs text-muted-foreground italic">*{note}</p>}

      {dialogOpt?.nutrition && (
        <NutritionDialog opt={dialogOpt} onClose={() => setDialogOpt(null)} />
      )}
    </div>
  );
}
